import React from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlaygroundIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FORCED_RANKING_SEARCH, MATCHING_VISUALIZER, RELEVANCE_TEST_DEFINITION_SEARCH, SETS_PLAYGROUND, SETS_CONFIG_SEARCH,
} from 'src/lib/routes';
import { LogoIcon } from './icons/Logo';

interface ToolOption {
    title: string;
    path: string;
    icon: React.ReactElement;
}

const tools: ToolOption[] = [
    { title: 'Ingest Configuration Tool', path: '/', icon: <SettingsIcon /> },
    { title: 'Search Relevance Scoring Tool', path: RELEVANCE_TEST_DEFINITION_SEARCH, icon: <SearchIcon /> },
    { title: 'Design Concept Forced Ranking Tool', path: FORCED_RANKING_SEARCH, icon: <TrendingUpIcon /> },
    { title: 'Sets Configuration Tool', path: SETS_CONFIG_SEARCH, icon: <TuneIcon /> },
    { title: 'Sets Playground', path: SETS_PLAYGROUND, icon: <PlaygroundIcon /> },
    { title: 'Matching Visualizer', path: MATCHING_VISUALIZER, icon: <VisibilityIcon /> },
];

interface ToolDrawerProps {
    open: boolean;
    onClose: () => void;
}

export const ToolDrawer = ({ open, onClose }: ToolDrawerProps): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleToolClick = (path: string): void => {
        navigate(path);
        onClose();
    };

    const isSelected = (toolPath: string): boolean => {
        if (toolPath === '/' && location.pathname === '/') return true;
        if (toolPath !== '/' && location.pathname.startsWith(toolPath)) return true;
        return false;
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
        >
            <Box
                sx={{
                    width: 420,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                    }}
                >
                    <Typography alignItems="center" display="flex" gap={4} variant="h6">
                        <LogoIcon size="sm" />
                        Design Relevance Tooling
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <List sx={{ flex: 1 }}>
                    {tools.map((tool) => (
                        <ListItem disablePadding key={tool.path}>
                            <ListItemButton
                                selected={isSelected(tool.path)}
                                onClick={(): void => handleToolClick(tool.path)}
                            >
                                <ListItemIcon>
                                    {tool.icon}
                                </ListItemIcon>
                                <ListItemText primary={tool.title} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Drawer>
    );
};

interface MenuButtonProps {
    onClick: () => void;
}

export const MenuButton = ({ onClick }: MenuButtonProps): JSX.Element => (
    <IconButton
        aria-label="open drawer"
        color="inherit"
        edge="start"
        onClick={onClick}
    >
        <MenuIcon />
    </IconButton>
);
